import React, { useCallback, useState, useEffect } from 'react';
import { validate as emailValidate } from 'email-validator';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    TextInput,
    Text,
    Checkbox,
    Button,
    H4,
} from '@revdotcom/vaporwave';

import { useSubmit } from '../useSubmit';
import { fixupEvents } from '../helpers';
import { type LoginPageProps } from '../../codegen/LoginPageProps';
import { type LoginPageSubmit } from '../../codegen/LoginPageSubmit';
import { Banner } from '../components/Banner';
import { CardLogo } from '../components/CardLogo';
import { UnderLinks } from '../components/UnderLinks';
import styles from '../../styles';
import { type ForgotPasswordProps } from '../../codegen/ForgotPasswordProps';
import { type FlowProps } from '../../FlowProps';
import { SocialLogins } from '../components/SocialLogins';

/**
 * Username/password page in the login flow.
 * @param {LoginPageProps} props page parameters
 * @returns {object} form component
 */
const LoginPage = (props: FlowProps<LoginPageProps>) => {
    const [searchParams] = useSearchParams();

    // enable react-hook-form form orchestration
    const {
        register,
        handleSubmit,
        setFocus,
        formState: { errors, isSubmitting },
        getValues,
        reset,
    } = useForm<LoginPageSubmit>({
        mode: 'onSubmit',
        criteriaMode: 'all',
        defaultValues: {
            userName: props.userName,
            persistent: props.persistent,
        },
    });

    const returnUrl = searchParams.get('returnUrl') ?? 'missing';

    // set up our ajax submit handler
    const { onSubmit, submitError, isRedirecting } = useSubmit('login', {
        captchaKey: props.captchaKey,
        antiforgeryToken: props.verificationToken,

        preSubmit: useCallback(
            (data: LoginPageSubmit) => {
                // post back
                data.returnUrl = returnUrl;
                data.passwordRequired = props.requirePassword;
                // make sure we never send password to the server, unless it was actually requested
                data.password = props.requirePassword
                    ? data.password
                    : undefined;
            },
            [returnUrl, props.requirePassword],
        ),
    });

    const formHeader = props.isLoginConfirmation ? 'Confirm login' : 'Sign in';
    const signUpLinkText =
        props.viewData['signup-link-text'] ??
        "Don't have an account? Click here to create one.";

    // "show password in clear" flag
    const [showPassword, setShowPassword] = useState(false);

    const displayPassword = props.requirePassword ? 'block' : 'none';
    const submitLabel = props.requirePassword
        ? props.isLoginConfirmation
            ? 'Confirm'
            : 'Sign in'
        : 'Next';

    // auto-focus
    useEffect(() => {
        // since we go from username to username+password via submit, that changes
        // error checking behavior (re-validation mode kicks in after first submit).
        // we want the second stage to work as an independent form, therefore reset
        // and set focus.
        reset(undefined, { keepValues: true, keepDefaultValues: true });
        setFocus(props.requirePassword ? 'password' : 'userName');
    }, [setFocus, reset, props.requirePassword]);

    const navigate = useNavigate();

    const onForgotPassword = useCallback(() => {
        const state: ForgotPasswordProps = {
            verificationToken: props.verificationToken,
            viewData: props.viewData,
            captchaKey: props.captchaKey,
            allowPersistent: props.allowPersistent,
            emailSent: false,
        };

        const encodedReturnUrl = encodeURIComponent(returnUrl);
        const encodedEmail = encodeURIComponent(getValues('userName'));
        const query = `returnUrl=${encodedReturnUrl}&email=${encodedEmail}`;
        navigate('/password/forgot?' + query, { state });
    }, [props, navigate, returnUrl, getValues]);

    // links provided by the page
    let signupLink = props.viewData['signup-link'];

    // if the signup link is routed via a flow event, instead of being an open link to elsewhere -
    // build actual local url, append returnUrl and known username (optionally)
    if (signupLink == 'event') {
        const username = getValues('userName');
        const queryString = new URLSearchParams(
            username ? { returnUrl, username } : { returnUrl },
        ).toString();

        signupLink = `/login/signup?${queryString}`;
    }

    return (
        <Card sx={styles.cardLogin}>
            <CardLogo {...props} />

            <div sx={styles.cardContainer}>
                <H4 sx={styles.cardTitle}>{formHeader}</H4>

                <Banner {...props} />

                <form sx={styles.cardForm} onSubmit={handleSubmit(onSubmit)}>
                    <div sx={{ position: 'relative' }}>
                        <TextInput
                            id="email-input"
                            label="Email address"
                            autoComplete="userName"
                            inputMode="email"
                            size="large"
                            type="email"
                            /*isRequired*/
                            validationState={errors.userName && 'invalid'}
                            helperText={
                                errors.userName &&
                                'Please enter a valid email address.'
                            }
                            sx={{ variant: 'forms.identity.login.userName' }}
                            {...fixupEvents(
                                register('userName', {
                                    required: true,
                                    validate: emailValidate,
                                }),
                            )}
                        />
                        {submitError && !props.requirePassword && (
                            <Text sx={styles.cardError}>{submitError}</Text>
                        )}
                    </div>

                    {/* make sure that password field is always present to fool password managers */}
                    {/* and we as well make sure that captured password is not submitted to the server unless it is required*/}
                    <div
                        sx={{ position: 'relative', display: displayPassword }}
                    >
                        <TextInput
                            id="password-input"
                            label="Password"
                            autoComplete="current-password"
                            size="large"
                            /*isRequired={props.requirePassword}*/
                            type={showPassword ? 'text' : 'password'}
                            validationState={errors.password && 'invalid'}
                            helperText={
                                errors.password && 'Please enter a password.'
                            }
                            sx={{
                                variant: 'forms.identity.login.password',
                            }}
                            {...fixupEvents(
                                register('password', {
                                    required: props.requirePassword,
                                }),
                            )}
                        />

                        <Button
                            id="show-password-button"
                            sizeVariant="minimal"
                            variant="text"
                            onPress={() => setShowPassword((f) => !f)}
                            sx={styles.cardShowPassword}
                        >
                            {showPassword ? 'Hide' : 'Show'}
                        </Button>

                        {submitError && props.requirePassword && (
                            <Text sx={styles.cardError}>{submitError}</Text>
                        )}
                    </div>

                    <div>
                        <Button
                            id="submit-button"
                            sizeVariant="small"
                            sx={{
                                display: 'block',
                                width: '100%',
                                fontSize: 1,
                                py: [2, 3],
                                variant: 'forms.identity.login.submit',
                            }}
                            type="submit"
                            isDisabled={isSubmitting || isRedirecting}
                        >
                            {submitLabel}
                        </Button>

                        <SocialLogins
                            returnUrl={returnUrl}
                            username={getValues('userName')}
                            {...props}
                        />
                    </div>
                </form>

                {props.allowPersistent && !props.isLoginConfirmation && (
                    <Checkbox
                        id="persist-checkbox"
                        sx={styles.cardRememberPassword}
                        {...fixupEvents(register('persistent'))}
                    >
                        Remember this device for 30 days
                    </Checkbox>
                )}

                {props.requirePassword && (
                    <Button
                        id="forgot-password-button"
                        sizeVariant="small"
                        variant="text"
                        onPress={onForgotPassword}
                        sx={styles.cardForgotPasswordLink}
                        isDisabled={isRedirecting}
                    >
                        Forgot your password?
                    </Button>
                )}

                {signupLink && !props.isLoginConfirmation && (
                    <Button
                        id="signup-button"
                        sizeVariant="small"
                        variant="text"
                        href={signupLink}
                        sx={styles.cardSignupLink}
                        isDisabled={isRedirecting}
                    >
                        {signUpLinkText}
                    </Button>
                )}
            </div>

            <UnderLinks {...props} />
        </Card>
    );
};

export default LoginPage;
